import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Hammer, Shield, MessageSquare, Star, ArrowRight, CheckCircle } from 'lucide-react';
import Button from '../components/ui/Button';

const HowItWorks: React.FC = () => {
  const steps = [
    {
      icon: <Search className="h-8 w-8" />,
      title: 'Find & book',
      description: 'Gennemse vores omfattende samling af værktøj fra betroede fællesskabsmedlemmer. Brug filtre til at finde præcis hvad du har brug for.',
      features: [
        'Søg efter kategori eller specifikt værktøj',
        'Filtrer efter lokation og tilgængelighed',
        'Sammenlign priser og anmeldelser',
        'Se detaljerede værktøjsbeskrivelser og fotos',
        'Fleksible lejeperioder'
      ]
    },
    {
      icon: <Calendar className="h-8 w-8" />,
      title: 'Afhent & betal',
      description: 'Mød værktøjsejeren, inspicér værktøjet, og få nødvendige instruktioner eller tips til brug.',
      features: [
        'Bekvem lokal afhentning',
        'Værktøjsinspektions-tjekliste',
        'Brugsretningslinjer leveret',
        'Kommunikation med ejer',
        'Direkte betaling ved afhentning'
      ]
    },
    {
      icon: <Hammer className="h-8 w-8" />,
      title: 'Brug værktøjet',
      description: 'Udfør dit projekt trygt og effektivt med det lånte værktøj. Følg sikkerhedsretningslinjer og kontakt ejeren ved spørgsmål.',
      features: [
        'Følg producentens brugsanvisning',
        'Brug passende sikkerhedsudstyr',
        'Kontakt ejer ved problemer eller spørgsmål',
        'Behandl værktøjet omhyggeligt'
      ]
    },
    {
      icon: <CheckCircle className="h-8 w-8" />,
      title: 'Returner & anmeld',
      description: 'Returner værktøjet i samme stand og del din oplevelse med fællesskabet.',
      features: [
        'Simpel returproces',
        'Standverificering',
        'Del hjælpsom feedback',
        'Opbyg tillid i fællesskabet'
      ]
    }
  ];

  const benefits = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: 'Sikkert & trygt',
      description: 'Hver udlejning er beskyttet af vores omfattende forsikringsdækning og sikre betalingssystem.'
    },
    {
      icon: <MessageSquare className="h-6 w-6" />,
      title: 'Direkte kommunikation',
      description: 'Indbygget beskedsystem tillader klar kommunikation mellem lånere og udlejere.'
    },
    {
      icon: <Star className="h-6 w-6" />,
      title: 'Betroet fællesskab',
      description: 'Verificerede brugere og anmeldelsessystem sikrer kvalitetsoplevelser for alle.'
    }
  ];

  const faqs = [
    {
      question: 'Hvad hvis et værktøj bliver beskadiget?',
      answer: 'Alle udlejninger inkluderer grundlæggende forsikringsdækning op til 2.500 kr.'
    },
    {
      question: 'Hvordan ved jeg at værktøjet virker ordentligt?',
      answer: 'Værktøjsejere er forpligtet til at vedligeholde deres værktøj og verificere fungerende stand før hver udlejning. Du kan også tjekke anmeldelser fra tidligere lejere.'
    },
    {
      question: 'Hvad er afbestillingspolitikken?',
      answer: 'Gratis afbestilling op til 24 timer før afhentning. Afbestillinger inden for 24 timer kan være underlagt et gebyr.'
    },
    {
      question: 'Hvordan betaler jeg for leje?',
      answer: 'Betaling af lejebeløbet aftales direkte mellem lejer og udlejer, og gennemføres ved afhentning af værktøjet.'
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="bg-green-600 text-white">
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Sådan virker DoMore
            </h1>
            <p className="mt-6 text-xl text-green-100">
              At låne og udlåne værktøj har aldrig været nemmere. Vores platform forbinder folk der har brug for værktøj 
              med dem der har det, og skaber et fællesskab af deling og støtte.
            </p>
            <div className="mt-8">
              <Link to='/signup'>
                <Button 
                  variant="outline"
                  size="lg"
                  icon={<ArrowRight className="h-5 w-5 ml-2" />}
                  className="border-white hover:text-white hover:bg-white hover:bg-opacity-10"
                >
                  Kom i gang
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <div className="py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Simpel 4-trins proces</h2>
            <p className="mt-4 text-xl text-gray-600">
              Vi har gjort værktøjsdeling ligetil og sikker
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-green-600 mb-4">{step.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 mb-4">{step.description}</p>
                <ul className="space-y-2">
                  {step.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Hvorfor vælge DoMore</h2>
            <p className="mt-4 text-xl text-gray-600">
              Vi prioriterer sikkerhed, tryghed og fællesskab
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs Section */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Ofte stillede spørgsmål</h2>
            <p className="mt-4 text-xl text-gray-600">
              Har du spørgsmål? Vi har svarene
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-green-600 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Klar til at komme i gang?</h2>
            <p className="text-xl text-green-100 mb-8">
              Bliv en del af vores fællesskab i dag og begynd at dele værktøj med dine naboer
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/browse">
                <Button 
                  variant="secondary"
                  size="lg"
                >
                  Gennemse værktøj
                </Button>
              </Link>
              <Link to="/signup">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white hover:text-white hover:bg-white hover:bg-opacity-10"
                >
                  Tilmeld dig nu
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;